import { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import moment from 'moment';

import {
  Box,
  Container,
  TextField,
  Select,
  MenuItem,
  Button,
  Autocomplete,
  // createFilterOptions
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { ProfileInfoHeader } from '../shared/ProfileInfoHeader';
import { FormPageNavBtns } from '../shared/FormPageNavBtns';
import { FormCardWrap } from '../shared/FormCardWrap';
import { useStyles } from './style';
import { dropDownConstList } from '../../config/dropDownConstList';

import { profileInfoAction } from '../../redux/profile/Reducer';

import { formCircleBtnNavLink } from '../../utils/generalFunc';

const insuranceDataPreLoad = (insuranceData, profileInsurerData) => insuranceData.find((eachInsurance) => eachInsurance.InsurancePlanName === profileInsurerData);
const defaultInsuranceObj = {
  InsurancePlanId: -1,
  InsurancePlanName: 'None',
  InsurancePlanState: 'None',
  IsAccepted: false,
};

const OthersInsuranceObj = {
  InsurancePlanId: -1,
  InsurancePlanName: 'Others',
  InsurancePlanState: 'Others',
  IsAccepted: false,
};

export const InsuranceDetailsForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const profile = useSelector((state) => state.profile.profilInfo);
  const insuranceData = useSelector((state) => state.profile.insuranceListing);
  const dispatch = useDispatch();
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [openAutoComplete, setOpenAutoComplete] = useState(false);

  useEffect(() => {
    setInsuranceOptions(insuranceData.map((eachInsurance) => {
      if ((typeof eachInsurance.InsurancePlanName) === 'string' && !!eachInsurance.InsurancePlanName) {
        let firstLetter = eachInsurance.InsurancePlanName[0].toUpperCase();
        let groupStmt = '';
        let groupStmtNo = 10;

        if (/[0-9]/.test(firstLetter)) firstLetter = '0-9';
        else if (/\W|_/.test(firstLetter)) firstLetter = '#$@...';

        if (eachInsurance.InsurancePlanState === dropDownConstList.stateList.find((stateVal) => stateVal.name === profile.State)?.abbreviation && !!eachInsurance.IsAccepted) {
          groupStmt = 'YOUR STATE';
          groupStmtNo = 1;
        } else if (eachInsurance.InsurancePlanState === '' && !!eachInsurance.IsAccepted) {
          groupStmt = 'All others';
          groupStmtNo = 2;
        } else if (eachInsurance.InsurancePlanState === '' && !eachInsurance.IsAccepted) {
          groupStmt = 'All others ';
          groupStmtNo = 3;
        } else if (eachInsurance.IsAccepted) {
          groupStmt = 'All others  ';
          groupStmtNo = 4;
        } else {
          groupStmt = 'All others   ';
          groupStmtNo = 5;
        }

        return {
          groupStmt,
          groupStmtNo,
          firstLetter,
          ...eachInsurance,
        };
      }
      return eachInsurance;
    }));
  }, []);

  const [dateVal, setDateVal] = useState(null);

  // no errors then disable or (if there is no values in required field and form is not touched then disable(in case of fresh form))
  const nextBtnDisabledFunc = (formik) => (Object.keys(formik.errors).length !== 0);

  const dateToShowStringFormat = (date) => {
    let fomattedDate = moment(date);
    if (fomattedDate.isValid()) {
      fomattedDate = fomattedDate.format('MM/DD/yyyy');
    } else {
      fomattedDate = '';
    }
    return fomattedDate;
  };

  const formik = useFormik({
    initialValues: {
      insuranceCarrier: insuranceDataPreLoad(insuranceData, profile.Insurer) ? insuranceDataPreLoad(insuranceData, profile.Insurer) : defaultInsuranceObj,
      carrierPh: profile.CarrierPhone || '',
      policyNo: profile.MemberID || '',
      groupNo: profile.Group || '',
      relationshipToPatient: profile.IsPatientSubscriber ? 'Self' : 'Other',
      birthdate: dateToShowStringFormat(profile.SubscriberBirthdate) || '',
      FnLName: profile.SubscriberName || '',
      gender: profile.SubscriberGender && dropDownConstList.genderList.includes(profile.SubscriberGender) ? profile.SubscriberGender : '',
      addr: profile.SubscriberAddress || '',
      phNo: '',
      employer: profile.SubscriberEmployee || '',
      workPh: '',
    },
    validationSchema: yup.object({
      insuranceCarrier: yup
        .object(),
      carrierPh: yup
        .string()
        .trim(),
      policyNo: yup
        .string()
        .trim(),
      groupNo: yup
        .string()
        .trim(),
      relationshipToPatient: yup
        .string()
        .trim(),
      birthdate: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /(0[1-9]|1[012])[ \/.](0[1-9]|[12][0-9]|3[01])[ \/.](19|20)\d\d/,
          'Enter a valid DOB'
        ),
      FnLName: yup
        .string()
        .trim(),
      gender: yup
        .string()
        .trim(),
      addr: yup
        .string()
        .trim(),
      phNo: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
          'Enter a valid phone number'
        ),
      employer: yup
        .string()
        .trim(),
      workPh: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
          'Enter a valid phone number'
        ),
    }),
    onSubmit: (data) => {
      // go into next page if statement if button is enabled
      if (!nextBtnDisabledFunc(formik)) {
        let birthDateToIsoString = '';
        if (data.birthdate) {
          birthDateToIsoString = new Date(data.birthdate).toISOString();
        }
        dispatch(profileInfoAction({
          ...profile,
          Insurer: data.insuranceCarrier.InsurancePlanName,
          CarrierPhone: data.carrierPh,
          MemberID: data.policyNo,
          Group: data.groupNo,
          IsPatientSubscriber: data.relationshipToPatient === 'Self',
          SubscriberBirthdate: birthDateToIsoString,
          SubscriberName: data.FnLName,
          SubscriberGender: data.gender,
          SubscriberAddress: data.addr,
          // phNo: data.phNo,
          SubscriberEmployee: data.employer,
          // workPh: data.workPh,
        }));
        history.push('/primary-care-info');
      }
    }
  });

  useEffect(() => {
    if (!(formik.values.insuranceCarrier)) {
      formik.setFieldValue('insuranceCarrier', defaultInsuranceObj);
    }
  }, [formik.values.insuranceCarrier]);

  const onDobChange = useCallback(
    (d) => {
      setDateVal(d);
      let fomattedDate = moment(d);
      if (fomattedDate.isValid()) {
        fomattedDate = fomattedDate.format('MM/DD/yyyy');
      } else {
        fomattedDate = '';
      }
      formik.setFieldValue('birthdate', fomattedDate);
    },
    [dateVal]
  );

  const onAutoCompleteChange = (event, newValue) => {
    formik.setFieldValue('insuranceCarrier', newValue);
  };

  const onAutoDropOtherOptionHandler = () => {
    formik.setFieldValue('insuranceCarrier', OthersInsuranceObj);
    setOpenAutoComplete(false);
  };

  const onDotClick = (dotNum) => {
    formCircleBtnNavLink(history, dotNum);
  };

  return (
    <Container maxWidth="md" className={`personalInfoForm ${classes.personalInfoContainer}`}>
      <ProfileInfoHeader />
      <FormCardWrap cardsBgNumber={2}>
        <form onSubmit={formik.handleSubmit} className="formContainer">
          <Box className={classes.personalContentWrap}>
            <Box className={classes.formPageNumber}>2/4</Box>
            <Box className={`${classes.carrierAndPhContainer} ${classes.subTextColor}`}>
              <Box className={`${classes.carrierContainer} autocompleteContainer`}>
                <Box className={`${classes.carrierLabel} commonLabel`} component="label">&nbsp;Insurance Carrier</Box>
                <Box style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Autocomplete
                    open={openAutoComplete}
                    onOpen={() => setOpenAutoComplete(true)}
                    onClose={() => setOpenAutoComplete(false)}
                    // filterOptions={filterOptions}
                    name="insuranceCarrier"
                    onChange={onAutoCompleteChange}
                    value={formik.values.insuranceCarrier || defaultInsuranceObj}
                    id="insuranceCarrier"
                    options={[
                      defaultInsuranceObj,
                      ...insuranceOptions.sort((a, b) => {
                        if ((typeof b.groupStmt) === 'string' && !!b.groupStmt && (typeof a.groupStmt) === 'string' && !!a.groupStmt) {
                          return -b.firstLetter.localeCompare(a.firstLetter);
                        }
                        return null;
                      }).sort((a, b) => a.groupStmtNo - b.groupStmtNo),
                      OthersInsuranceObj
                    ]}
                    groupBy={(option) => option.groupStmt}
                    classes={{ paper: classes.autoCompleteList }}
                    getOptionLabel={(option) => `${option.InsurancePlanName}${option.InsurancePlanId < 0 ? '' : ` (${option.InsurancePlanId})`}`}
                    sx={{ width: '100%', mt: '5px' }}
                    renderInput={(params) => <TextField {...params} placeholder="Insurance Carrier" label="" />}
                    noOptionsText={<span className={classes.othersAutoCompleteOption} onKeyDown={onAutoDropOtherOptionHandler} onClick={onAutoDropOtherOptionHandler} role="button" tabIndex={0}>Others</span>}
                  />
                </Box>
              </Box>
              <Box className={classes.primaryLangContainer}>
                <Box className="commonLabel" component="label">&nbsp;Carrier Phone #</Box>
                <TextField
                  name="carrierPh"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('carrierPh')}
                  value={formik.values.carrierPh || ''}
                  className="inputFormTopMargin"
                  id="carrierPh"
                  placeholder="Carrier Phone #"
                  variant="outlined"
                />
              </Box>
            </Box>
            <Box className={`${classes.policyGroupContainer} ${classes.subTextColor}`}>
              <Box className={classes.policyContainer}>
                <Box className={`${classes.policyLabel} commonLabel`} component="label">&nbsp;Policy #</Box>
                <TextField
                  name="policyNo"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('policyNo')}
                  value={formik.values.policyNo || ''}
                  className="inputFormTopMargin"
                  id="policyNo"
                  placeholder="Policy #"
                  variant="outlined"
                />
              </Box>
              <Box className={classes.fullWidth}>
                <Box className="commonLabel" component="label">&nbsp;Group #</Box>
                <TextField
                  name="groupNo"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('groupNo')}
                  value={formik.values.groupNo || ''}
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="Group #"
                  variant="outlined"
                />
              </Box>
            </Box>
            <Box className={classes.contentTitleWrap}>
              <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine1}`} />
              <Box className={classes.contentTitle}>Policy Holder Info</Box>
              <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine2}`} />
            </Box>
            <Box className={`${classes.patientRelDobContainer} ${classes.subTextColor}`}>
              <Box className={classes.patientRelContainer}>
                <Box className={`${classes.tooltipLabel} commonLabel`} component="label">
                  &nbsp;Relationship to Patient
                </Box>
                <Select
                  className="inputFormTopMargin"
                  name="relationshipToPatient"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('relationshipToPatient')}
                  value={formik.values.relationshipToPatient || ''}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  placeholder="State"
                >
                  <MenuItem value="">
                    None
                  </MenuItem>
                  {dropDownConstList.relationshipToPatientList.map((value) => <MenuItem value={value} key={value}>{value}</MenuItem>)}
                </Select>
              </Box>
              <Box className={classes.fullWidth}>
                <Box className={`${classes.tooltipLabel} commonLabel`} component="label">
                  &nbsp;Birthdate
                </Box>
                <Box className="inputFormTopMargin">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      value={formik.values.birthdate || ''}
                      onChange={onDobChange}
                      renderInput={(params) => <TextField
                        {...params}
                        onBlur={() => formik.setFieldTouched('birthdate')}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: 'Birthdate',
                        }}
                      />}
                    />
                  </LocalizationProvider>
                  {formik.touched.birthdate && formik.errors.birthdate && (
                    <div className={classes.errorText}>{formik.errors.birthdate}</div>
                  )}
                </Box>
              </Box>
            </Box>
            <Box className={`${classes.nameGenderContainer} ${classes.subTextColor}`}>
              <Box className={classes.nameContainer}>
                <Box className={`${classes.tooltipLabel} commonLabel`} component="label">
                  &nbsp;First &amp; Last Name
                </Box>
                <TextField
                  className="inputFormTopMargin"
                  name="FnLName"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('FnLName')}
                  value={formik.values.FnLName || ''}
                  id="FnLName"
                  placeholder="First &amp; Last Name"
                  variant="outlined"
                />
              </Box>
              <Box className={classes.fullWidth}>
                <Box className={`${classes.tooltipLabel} commonLabel`} component="label">
                  &nbsp;Gender
                </Box>
                <Select
                  className="inputFormTopMargin"
                  name="gender"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('gender')}
                  value={formik.values.gender || ''}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  placeholder="Gender"
                >
                  <MenuItem value="">
                    None
                  </MenuItem>
                  {dropDownConstList.genderList.map((value) => <MenuItem value={value} key={value}>{value}</MenuItem>)}
                </Select>
              </Box>
            </Box>
            <Box className={`${classes.addrPhContainer} ${classes.subTextColor}`}>
              <Box className={classes.addrContainer}>
                <Box className={`${classes.addrLabel} commonLabel`} component="label">&nbsp;Address</Box>
                <TextField
                  name="addr"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('addr')}
                  value={formik.values.addr || ''}
                  className="inputFormTopMargin"
                  id="addr"
                  placeholder="Address"
                  variant="outlined"
                />
              </Box>
              <Box className={classes.fullWidth}>
                <Box className="commonLabel" component="label">&nbsp;Phone</Box>
                <TextField
                  className="inputFormTopMargin"
                  name="phNo"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('phNo')}
                  value={formik.values.phNo || ''}
                  id="phNo"
                  placeholder="Phone"
                  variant="outlined"
                />
                {formik.touched.phNo && formik.errors.phNo && (
                  <div className={classes.errorText}>{formik.errors.phNo}</div>
                )}
              </Box>
            </Box>
            <Box className={`${classes.addrPhContainer} ${classes.subTextColor}`}>
              <Box className={classes.addrContainer}>
                <Box className={`${classes.addrLabel} commonLabel`} component="label">&nbsp;Employer</Box>
                <TextField
                  name="employer"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('employer')}
                  value={formik.values.employer || ''}
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="Employer"
                  variant="outlined"
                />
              </Box>
              <Box className={classes.fullWidth}>
                <Box className="commonLabel" component="label">&nbsp;Work Phone</Box>
                <TextField
                  name="workPh"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('workPh')}
                  value={formik.values.workPh || ''}
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="Work Phone"
                  variant="outlined"
                />
                {formik.touched.workPh && formik.errors.workPh && (
                  <div className={classes.errorText}>{formik.errors.workPh}</div>
                )}
              </Box>
            </Box>
            <Box className={classes.buttonsNavContainer}>
              <Button onClick={() => history.push('/personal-info')} variant="contained" color="secondary">
                Back
              </Button>
              <FormPageNavBtns formPageNumber={4} activePgNum={2} onDotClick={onDotClick} />
              <Button
                className={nextBtnDisabledFunc(formik) ? classes.btnDisabled : ''}
                type="submit"
                variant="contained"
                color="primary"
              >
                Next
              </Button>
            </Box>
          </Box>
        </form>
      </FormCardWrap>
    </Container>
  );
};
